import { Box, IconButton, Tooltip } from '@mui/material';
import { ScreenshotMonitor } from '@mui/icons-material';
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { COLORS } from '../../services/constants/colors';

export interface EasScreenshotFrameRef {
	takeScreenshot: () => Promise<void>;
}

interface EasScreenshotFrameProps {
	children: React.ReactNode;
	id: string;
	title: string;
	button?: boolean;
	frame?: boolean;
}

export const EasScreenshotFrame = forwardRef<EasScreenshotFrameRef, EasScreenshotFrameProps>(
	({ children, id, title, button = false, frame = false }, ref) => {
		const [hover, setHover] = useState(false);
		const [shot, setShot] = useState(false);
		const shotInProgress = useRef(false);

		const generateScreenshot = useCallback(async () => {
			const element = document.getElementById(`${id}-frame`);
			if (!element) return;

			const canvas = await html2canvas(element, {
				scale: 2,
				useCORS: true,
				backgroundColor: '#ffffff',
			});

			const link = document.createElement('a');
			link.download = `${title}.png`;
			link.href = canvas.toDataURL('image/png');
			link.click();
		}, [id, title]);

		const handleShot = useCallback(async () => {
			if (shotInProgress.current) return;
			shotInProgress.current = true;

			setShot(true);
			await generateScreenshot();
			setTimeout(() => {
				setShot(false);
				shotInProgress.current = false;
			}, 400);
		}, [generateScreenshot]);

		useImperativeHandle(ref, () => ({ takeScreenshot: handleShot }), [handleShot]);

		const onMouseEnter = useCallback(() => setHover(true), []);
		const onMouseLeave = useCallback(() => setHover(false), []);

		return (
			<Box
				id={`${id}-frame`}
				sx={{
					position: 'relative',
					border: !frame ? 'none' : hover ? '2px solid #00000020' : '2px solid transparent',
					borderRadius: '12px',
					// padding: '1px',
					margin: !frame ? '0' : '1px',
					transition: 'border 0.3s ease-in-out',
					height: 'fit-content',
				}}
			>
				<Box
					sx={{
						position: 'absolute',
						inset: 0,
						borderRadius: 'inherit',
						backgroundColor: COLORS.PAPER,
						opacity: shot ? 0.85 : 0,
						transition: 'opacity 0.3s ease-in-out',
						pointerEvents: 'none',
						zIndex: 1,
					}}
				/>
				{button && (
					<Box sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center' }}>
						<Tooltip title="Generate Screenshot">
							<IconButton
								onClick={handleShot}
								color="primary"
								sx={{ p: '0 10px', m: 0 }}
								onMouseEnter={onMouseEnter}
								onMouseLeave={onMouseLeave}
							>
								<ScreenshotMonitor sx={{ fontSize: '1.5rem', p: 0, m: 0, color: COLORS.PRIMARY }} />
							</IconButton>
						</Tooltip>
					</Box>
				)}
				{children}
			</Box>
		);
	},
);

EasScreenshotFrame.displayName = 'EasScreenshotFrame';
